import { useCallback, useState, useEffect } from 'react';
import { debounce } from '@utils/common';

const SUPPORTS_SELECTION = typeof document !== "undefined" && typeof document.getSelection === "function";

/**
 * keep track of current user selection, allowing components to easily react
 * directly to user selection.
 */
function useSelectionRange(ancestorSelector, options) {
  // helper to extract current selection range from the document
  const extractRangeFromSelection = useCallback(() => {
    const selection = document.getSelection();
    if (selection === null || selection.rangeCount === 0) {
      return;
    }
    const firstRange = selection.getRangeAt(0);
    const lastRange = selection.getRangeAt(selection.rangeCount - 1);
    const selectionRange = document.createRange();
    selectionRange.setStart(firstRange.startContainer, firstRange.startOffset);
    selectionRange.setEnd(lastRange.endContainer, lastRange.endOffset);
    if (selectionRange.collapsed || selectionRange.toString().trim().length === 0) {
      // ignore empty selection ranges
      return;
    }
    if (ancestorSelector) {
      // if an ancestor selector was given, then verify that the selection is
      // contained inside this ancestor
      const ancestor = Array.from(document.querySelectorAll(ancestorSelector)).find(ancestor => ancestor === selectionRange.commonAncestorContainer || ancestor.contains(selectionRange.commonAncestorContainer));
      if (!ancestor) {
        return;
      }
    }
    return selectionRange.cloneRange();
  }, [ancestorSelector]);
  const [selectionRange, setSelectionRange] = useState(extractRangeFromSelection());

  // keep track of current selection, so we can always return a fresh reference
  // to the currently selected node
  useEffect(() => {
    if (!SUPPORTS_SELECTION || !options?.enabled) {
      setSelectionRange(undefined);
      return;
    }

    // debounce updated by 300ms, we don't need to constantly refresh the
    // selection as user will need a bit of time
    const handleSelectionChange = debounce(() => {
      setSelectionRange(extractRangeFromSelection());
    }, 300);
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [extractRangeFromSelection, options?.enabled]);
  return selectionRange;
}

export { useSelectionRange };
//# sourceMappingURL=useSelectionRange.js.map
